/**
 * AI 智能分类 · 顶层文件夹列表（数据源 slice）
 *
 * 持有「分类文件夹」页根目录（parentFolderID=0）的文件夹列表，作为该列表的
 * 单一数据源。原先列表数据放在 FolderList 组件本地 useState，现提升到 store：
 *  · 父级 AIClassifyPage 据「是否有文件夹」决定顶部工具栏显隐
 *  · 未来兄弟组件也可直接订阅，无需逐层透传
 *
 * 低层取数（fetchAIFolders / fetchFolderStatistic）仍在各自模块，本 slice 只
 * 负责「编排两段请求 + 适配 + 存储」。子文件夹（展开）仍由 FolderList 本地管理。
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '../../app/store'
import { fetchFolderStatistic } from '../../actions/folder'
import { fetchAIFolders } from './folderApiSlice'
import { adaptFolderList } from './adaptFolder'
import type { FolderListItem } from '../../types/ai-classify'

interface FolderListState {
  /** 顶层文件夹（已 adapt）· 不含「待归档」虚拟项，虚拟项由视图层派生 */
  items: FolderListItem[]
  /** 未归类（待归档）照片数 · 来自 /web/folder/ai/get 根请求 */
  unclassifiedPhotoCount: number
  /** 是否展示「待归档」入口 · 来自 /web/folder/ai/get 根请求，服务端控制显隐 */
  showUnclassifiedFolder: boolean
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  /** 最近一次 pending 的 requestId · 用于 last-write-wins 竞态兜底 */
  currentRequestId: string | undefined
}

const initialState: FolderListState = {
  items: [],
  unclassifiedPhotoCount: 0,
  showUnclassifiedFolder: false,
  status: 'idle',
  error: null,
  currentRequestId: undefined
}

/** loadAIFolderList 成功载荷 */
interface FolderListPayload {
  items: FolderListItem[]
  unclassifiedPhotoCount: number
  showUnclassifiedFolder: boolean
}

/**
 * 加载顶层文件夹列表 · 两段串行（列表 → 统计）后适配为 FolderListItem[]。
 *
 * 失败语义：fetchAIFolders 失败时 resolve undefined（不 throw），这里转 reject。
 * 列表为空时跳过 statistic 请求，直接落空态。
 */
export const loadAIFolderList = createAsyncThunk<
  FolderListPayload,
  { groupID: number | string },
  { state: RootState; rejectValue: string }
>(
  'aiClassify/folderList/load',
  async ({ groupID }, { dispatch, rejectWithValue }) => {
    const result = await dispatch(
      fetchAIFolders({ groupID, parentFolderID: 0 })
    )
    if (!result) return rejectWithValue('加载失败')

    const list = result.folders
    if (list.length === 0) {
      return {
        items: [],
        unclassifiedPhotoCount: result.unclassifiedPhotoCount,
        showUnclassifiedFolder: result.showUnclassifiedFolder
      }
    }

    const folderIDs = list.map((f) => f.folderID)
    // fetchFolderStatistic 是旧 JS action，返回体形如 { data: [...] }；可缺时兜底空数组
    const statResp = (await dispatch(
      fetchFolderStatistic({ groupID, folderIDs })
    )) as { data?: unknown[] } | undefined
    const stats = Array.isArray(statResp?.data) ? statResp!.data : []

    return {
      // raw 列表类型窄声明（RawAIFolder 仅含 folderID），运行时含全字段，沿用 as never[]
      items: adaptFolderList(list as never[], stats as never[]),
      unclassifiedPhotoCount: result.unclassifiedPhotoCount,
      showUnclassifiedFolder: result.showUnclassifiedFolder
    }
  }
)

const folderListSlice = createSlice({
  name: 'aiClassify/folderList',
  initialState,
  reducers: {
    /**
     * 手动重置为初始态 · 外部兜底入口（如登出 / 需强制清空时调用）。
     *
     * 刻意不监听 teamSwitched 自动重置：切团队时 groupID 变化会让 FolderList 重新
     * 派发 loadAIFolderList（重载期间 status=loading，旧列表被 Spin 盖住、不闪现），
     * 且 requestId 守卫保证后发先至。若再监听 teamSwitched 重置，会与 mount 期父级
     * useAIClassifySession 派发的 teamSwitched 抢跑——子 effect 先发起加载、父 effect
     * 随后 teamSwitched 清掉 currentRequestId，进行中的 fulfilled 被守卫丢弃，导致
     * 列表一直转圈不显示（见 test/folderListSlice.test.js）。
     */
    resetFolderList: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAIFolderList.pending, (state, action) => {
        state.status = 'loading'
        state.error = null
        // 记录本次请求 id；不清空 items，让后台 refresh 时列表/工具栏不闪
        state.currentRequestId = action.meta.requestId
      })
      .addCase(loadAIFolderList.fulfilled, (state, action) => {
        // 旧请求晚于新请求返回 → 丢弃，保证 last-write-wins
        if (action.meta.requestId !== state.currentRequestId) return
        state.items = action.payload.items
        state.unclassifiedPhotoCount = action.payload.unclassifiedPhotoCount
        state.showUnclassifiedFolder = action.payload.showUnclassifiedFolder
        state.status = 'succeeded'
        state.currentRequestId = undefined
      })
      .addCase(loadAIFolderList.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return
        state.status = 'failed'
        state.error = action.payload ?? '加载失败'
        state.currentRequestId = undefined
      })
    // 注意：不监听 teamSwitched 自动重置 · 原因见 resetFolderList 注释
  }
})

export const { resetFolderList } = folderListSlice.actions

export default folderListSlice.reducer
